import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { empAddressService } from '@/services/empAddressService'
import { employeesService } from '@/services/employeesService'
import { customersService } from '@/services/customersService'
import type { Employee, CountryLookup, CityLookup, EmpAddressVm } from '@/api/types'
import { showError, showSuccess } from '@/lib/swal'

export const EmpAddressEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const ta = (key: string) => t(`empAddress.${key}`)
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')
  const listEmpId = useMemo(() => {
    const n = Number(searchParams.get('empId') ?? searchParams.get('EmpId') ?? 0)
    return n > 0 && !Number.isNaN(n) ? n : 0
  }, [searchParams])
  const backToList = useCallback(() => {
    navigate(listEmpId ? `/emp-addresses?empId=${listEmpId}` : '/emp-addresses')
  }, [navigate, listEmpId])

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<Employee[]>([])
  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [cities, setCities] = useState<CityLookup[]>([])

  const [form, setForm] = useState({
    empId: 0,
    zipCode: '',
    poBox: '',
    countryId: 0,
    cityId: 0,
    district: '',
    street: '',
    building: '',
    apartment: '',
    homePhoneNumber: '',
    addressDetail: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const empRes = await employeesService.getAllEmployees(branchId, 1, 500)
        setEmployees(empRes.data ?? [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    if (!isEditing && listEmpId) {
      setForm((prev) => (prev.empId === listEmpId ? prev : { ...prev, empId: listEmpId }))
    }
  }, [isEditing, listEmpId])

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countryRes = await customersService.getCountries()
        setCountries(countryRes ?? [])
      } catch {
        setCountries([])
      }
    }
    loadCountries()
  }, [])

  useEffect(() => {
    if (form.countryId && form.countryId > 0) {
      const loadCities = async () => {
        try {
          const citiesData = await customersService.getCities(form.countryId)
          setCities(citiesData ?? [])
        } catch {
          setCities([])
        }
      }
      loadCities()
    } else {
      setCities([])
    }
  }, [form.countryId])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await empAddressService.getById(Number(id))
        setForm({
          empId: data.empId ?? 0,
          zipCode: String(data.zipCode ?? ''),
          poBox: String(data.poBox ?? ''),
          countryId: data.countryId ?? 0,
          cityId: data.cityId ?? 0,
          district: data.district ?? '',
          street: data.street ?? '',
          building: data.building ?? '',
          apartment: data.apartment ?? '',
          homePhoneNumber: String(data.homePhoneNumber ?? ''),
          addressDetail: data.addressDetail ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? ta('loadFailed'))
        backToList()
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, backToList])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn]
          .filter(Boolean)
          .join(' ')
          .trim()
        const label = fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? `Emp ${e.id}`
        return { value: String(e.id), label }
      }),
    [employees]
  )

  const countryOptions = useMemo(
    () =>
      countries.map((c) => ({
        value: String(c.id ?? 0),
        label: c.lookupNameEn ?? c.lookupNameAr ?? `Country ${c.id}`,
      })),
    [countries]
  )

  const cityOptions = useMemo(
    () =>
      cities.map((c) => ({
        value: String(c.id ?? 0),
        label: c.lookupNameEn ?? c.lookupNameAr ?? `City ${c.id}`,
      })),
    [cities]
  )

  const employeeSelectLocked = listEmpId > 0
  const displayEmpCode = useMemo(() => {
    const emp = employees.find((e) => e.id === form.empId)
    return emp?.empCode?.trim() || '—'
  }, [employees, form.empId])

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error(ta('branchRequired'))
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error(ta('employeeRequired'))
      return
    }
    if (!form.homePhoneNumber.trim()) {
      toast.error(ta('homePhoneRequired'))
      return
    }
    if (!form.addressDetail.trim()) {
      toast.error(ta('addressDetailRequired'))
      return
    }

    const toNumberOrZero = (value: string) => {
      const n = Number(value)
      return Number.isFinite(n) ? n : 0
    }

    const payload: EmpAddressVm = {
      id: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      zipCode: toNumberOrZero(form.zipCode),
      poBox: toNumberOrZero(form.poBox),
      countryId: form.countryId || 0,
      cityId: form.cityId || 0,
      district: form.district,
      street: form.street,
      building: form.building,
      apartment: form.apartment,
      homePhoneNumber: toNumberOrZero(form.homePhoneNumber),
      addressDetail: form.addressDetail,
      branchId: branchId ?? 0,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await empAddressService.update(payload)
        await showSuccess(ta('updatedTitle'), ta('updatedMessage'))
      } else {
        await empAddressService.create(payload)
        await showSuccess(ta('savedTitle'), ta('savedMessage'))
      }
      backToList()
    } catch (e: any) {
      await showError(ta('saveFailedTitle'), e?.response?.data?.message ?? e?.message ?? ta('saveFailedMessage'))
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={ta('formTitle')} description={ta('formDescriptionGeneric')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">{ta('selectBranchFirst')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={ta('formTitle')}
        description={isEditing ? ta('formDescriptionEdit') : ta('formDescriptionAdd')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{ta('cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="emp-code-display">{ta('labelEmpCode')}</Label>
              <Input id="emp-code-display" readOnly value={displayEmpCode} className="bg-muted/50" aria-readonly />
            </div>
            <div className="space-y-2">
              <Label>
                {ta('requiredMark')} {ta('labelEmployee')}
              </Label>
              <SearchableSelect
                options={[{ value: '0', label: ta('placeholderSelectEmployee') }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => {
                  if (employeeSelectLocked) return
                  handleChange({ empId: Number(v) || 0 })
                }}
                placeholder={ta('placeholderSelectEmployee')}
                searchPlaceholder={ta('searchEmployeePlaceholder')}
                emptyMessage={ta('emptyEmployee')}
                disabled={employeeSelectLocked}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ta('requiredMark')} {ta('labelZipCode')}
              </Label>
              <Input
                value={form.zipCode}
                onChange={(e) => handleChange({ zipCode: e.target.value })}
                placeholder={ta('placeholderZipCode')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ta('requiredMark')} {ta('labelPoBox')}
              </Label>
              <Input
                value={form.poBox}
                onChange={(e) => handleChange({ poBox: e.target.value })}
                placeholder={ta('placeholderPoBox')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ta('requiredMark')} {ta('labelCountry')}
              </Label>
              <SearchableSelect
                options={[{ value: '0', label: ta('placeholderCountry') }, ...countryOptions]}
                value={form.countryId ? String(form.countryId) : '0'}
                onValueChange={(v) => handleChange({ countryId: Number(v) || 0, cityId: 0 })}
                placeholder={ta('placeholderCountry')}
                searchPlaceholder={ta('searchCountryPlaceholder')}
                emptyMessage={ta('emptyCountry')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ta('requiredMark')} {ta('labelCity')}
              </Label>
              <SearchableSelect
                options={[{ value: '0', label: ta('placeholderCity') }, ...cityOptions]}
                value={form.cityId ? String(form.cityId) : '0'}
                onValueChange={(v) => handleChange({ cityId: Number(v) || 0 })}
                placeholder={ta('placeholderCity')}
                searchPlaceholder={ta('searchCityPlaceholder')}
                emptyMessage={ta('emptyCity')}
                disabled={!form.countryId || form.countryId === 0}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ta('requiredMark')} {ta('labelDistrict')}
              </Label>
              <Input
                value={form.district}
                onChange={(e) => handleChange({ district: e.target.value })}
                placeholder={ta('placeholderDistrict')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ta('requiredMark')} {ta('labelStreet')}
              </Label>
              <Input
                value={form.street}
                onChange={(e) => handleChange({ street: e.target.value })}
                placeholder={ta('placeholderStreet')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ta('requiredMark')} {ta('labelBuilding')}
              </Label>
              <Input
                value={form.building}
                onChange={(e) => handleChange({ building: e.target.value })}
                placeholder={ta('placeholderBuilding')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ta('requiredMark')} {ta('labelApartment')}
              </Label>
              <Input
                value={form.apartment}
                onChange={(e) => handleChange({ apartment: e.target.value })}
                placeholder={ta('placeholderApartment')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {ta('requiredMark')} {ta('labelHomePhone')}
              </Label>
              <Input
                value={form.homePhoneNumber}
                onChange={(e) => handleChange({ homePhoneNumber: e.target.value })}
                placeholder={ta('placeholderHomePhone')}
              />
            </div>
            <div className="space-y-2 lg:col-span-2">
              <Label>
                {ta('requiredMark')} {ta('labelAddressDetail')}
              </Label>
              <Input
                value={form.addressDetail}
                onChange={(e) => handleChange({ addressDetail: e.target.value })}
                placeholder={ta('placeholderAddressDetail')}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => backToList()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {ta('return')}
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {isEditing ? t('common.update') : t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

